'use client';

// ============================================================================
// CrewAhead Maritime Platform - useJobs Hook
// ============================================================================

import { useCallback, useMemo } from 'react';
import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { useAuth } from './useAuth';
import {
  getPublicJobs as getPublicJobsApi,
  getJob as getJobApi,
  applyToJob as applyToJobApi,
  getApplications as getApplicationsApi,
  withdrawApplication as withdrawApplicationApi,
  getSavedJobs as getSavedJobsApi,
  saveJob as saveJobApi,
  unsaveJob as unsaveJobApi,
  isJobSaved as isJobSavedApi,
  createJobAlert as createJobAlertApi,
  getJobAlerts as getJobAlertsApi,
  updateJobAlert as updateJobAlertApi,
  deleteJobAlert as deleteJobAlertApi,
  toggleJobAlert as toggleJobAlertApi,
} from '../lib/api/jobs';
import type { CrewAheadApiError } from '../lib/api/client';
import { QUERY_KEYS } from '../lib/types/api';
import type {
  JobAlert,
  JobAlertCreateData,
  JobApplication,
  JobFilters,
  JobPost,
  PaginationParams,
} from '../lib/types';
import type { JobApplicationInput } from '../lib/api/jobs';

// Local type — used by the apply callback (mirrors JobApplicationInput shape)
type JobApplicationCreateData = {
  cover_letter?: string;
  expected_salary?: number;
  availability_date?: string;
};

// ─── Hook Return Type ────────────────────────────────────────────────────────

export interface UseJobsReturn {
  // ─── Job Discovery ────────────────────────────────────────────────────────
  jobs: JobPost[];
  jobsPagination: { count: number; next: string | null; previous: string | null } | null;
  isJobsLoading: boolean;
  jobsError: CrewAheadApiError | null;
  refetchJobs: () => Promise<void>;

  // ─── Single Job ───────────────────────────────────────────────────────────
  job: JobPost | null;
  isJobLoading: boolean;
  jobError: CrewAheadApiError | null;

  // ─── Job Application ──────────────────────────────────────────────────────
  apply: (jobId: string, data?: JobApplicationCreateData) => Promise<JobApplication>;
  isApplying: boolean;
  applications: JobApplication[];
  applicationsPagination: { count: number; next: string | null; previous: string | null } | null;
  isApplicationsLoading: boolean;
  withdrawApplication: (applicationId: string) => Promise<void>;
  isWithdrawing: boolean;

  // ─── Saved Jobs ───────────────────────────────────────────────────────────
  savedJobs: JobPost[];
  savedJobsPagination: { count: number; next: string | null; previous: string | null } | null;
  isSavedJobsLoading: boolean;
  saveJob: (jobId: string) => Promise<void>;
  unsaveJob: (jobId: string) => Promise<void>;
  isSaving: boolean;
  isJobSaved: (jobId: string) => Promise<boolean>;

  // ─── Job Alerts ───────────────────────────────────────────────────────────
  jobAlerts: JobAlert[];
  isJobAlertsLoading: boolean;
  createJobAlert: (data: JobAlertCreateData) => Promise<JobAlert>;
  isCreatingAlert: boolean;
  updateJobAlert: (alertId: string, data: Partial<JobAlertCreateData>) => Promise<JobAlert>;
  deleteJobAlert: (alertId: string) => Promise<void>;
  toggleJobAlert: (alertId: string, isActive: boolean) => Promise<JobAlert>;
  isMutatingAlert: boolean;
}

// ─── useJobs Hook ────────────────────────────────────────────────────────────

/**
 * Hook for managing job discovery, applications, saved jobs, and alerts.
 *
 * Uses TanStack Query for caching and optimistic updates. All data
 * is automatically synchronized across the application.
 *
 * @param filters - Job search filters
 * @param pagination - Pagination parameters
 * @param options - Additional query options
 *
 * @example
 * ```tsx
 * const {
 *   jobs,
 *   isJobsLoading,
 *   apply,
 *   savedJobs,
 *   saveJob,
 *   unsaveJob,
 * } = useJobs({ keywords: 'chief officer', vesselType: 'container_ship' });
 *
 * return (
 *   <div>
 *     <JobList jobs={jobs} onApply={apply} onSave={saveJob} />
 *     <SavedJobsSidebar jobs={savedJobs} onUnsave={unsaveJob} />
 *   </div>
 * );
 * ```
 */
export function useJobs(
  filters: JobFilters = {},
  pagination: PaginationParams = {},
  options?: {
    jobId?: string; // For single job view
    queryOptions?: Omit<UseQueryOptions<{ count: number; results: JobPost[] }, CrewAheadApiError>, 'queryKey' | 'queryFn'>;
    enabled?: boolean;
  }
): UseJobsReturn {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isJobSeeker = user?.role === 'seafarer' || user?.role === 'shore';
  const canUseSavedJobs = isJobSeeker;

  // ─── Jobs List Query ───────────────────────────────────────────────────────

  const {
    data: jobsData,
    isLoading: isJobsLoading,
    error: jobsError,
    refetch: refetchJobsQuery,
  } = useQuery<{ count: number; results: JobPost[] }, CrewAheadApiError>({
    queryKey: QUERY_KEYS.JOBS.LIST({ ...filters, ...pagination }),
    queryFn: async () => {
      const type = filters?.rankType === 'shore' ? 'shore' : 'onboard';
      const response = await getPublicJobsApi({
        type,
        rankId: filters?.rank,
        vesselTypeId: filters?.vesselType,
        location: filters?.location,
        page: (pagination?.page as number | undefined) ?? filters?.page,
        pageSize: (pagination?.limit as number | undefined) ?? filters?.limit,
      });
      return { count: response.count, results: response.results };
    },
    enabled: options?.enabled !== false,
    staleTime: 1000 * 60 * 2,
    retry: 0,
    ...options?.queryOptions,
  });

  // ─── Single Job Query ──────────────────────────────────────────────────────

  const {
    data: singleJobData,
    isLoading: isSingleJobLoading,
    error: singleJobError,
  } = useQuery({
    queryKey: options?.jobId ? QUERY_KEYS.JOBS.DETAIL(options.jobId) : ['job', 'none'],
    queryFn: () => {
      if (!options?.jobId) throw new Error('No job ID provided');
      return getJobApi(options.jobId);
    },
    enabled: !!options?.jobId,
    staleTime: 1000 * 60 * 3,
  });

  // ─── Applications Query ────────────────────────────────────────────────────

  const {
    data: applicationsData,
    isLoading: isApplicationsLoading,
  } = useQuery<{ count: number; results: JobApplication[] }>({
    queryKey: QUERY_KEYS.JOBS.APPLICATIONS,
    queryFn: () => getApplicationsApi(pagination as Record<string, unknown>),
    enabled: canUseSavedJobs && options?.enabled !== false,
    staleTime: 1000 * 60 * 2,
    retry: 0,
  });

  // ─── Saved Jobs Query ──────────────────────────────────────────────────────
  // getSavedJobsApi() returns { count, results: JobPost[] }

  const {
    data: savedJobsRaw,
    isLoading: isSavedJobsLoading,
  } = useQuery<{ count: number; results: JobPost[] }>({
    queryKey: QUERY_KEYS.JOBS.SAVED,
    queryFn: () => getSavedJobsApi(),
    enabled: canUseSavedJobs && options?.enabled !== false,
    staleTime: 1000 * 60 * 2,
    retry: 0,
  });

  // ─── Job Alerts Query ──────────────────────────────────────────────────────

  const {
    data: jobAlertsData,
    isLoading: isJobAlertsLoading,
  } = useQuery({
    queryKey: QUERY_KEYS.JOBS.ALERTS,
    queryFn: getJobAlertsApi,
    enabled: canUseSavedJobs && options?.enabled !== false,
    staleTime: 1000 * 60 * 5,
    retry: 0,
  });

  // ─── Apply Mutation ────────────────────────────────────────────────────────

  const applyMutation = useMutation<JobApplication, CrewAheadApiError, {
    jobId: string;
    data: JobApplicationInput;
  }>({
    mutationFn: ({ jobId, data }) => applyToJobApi(jobId, data as JobApplicationInput),
    onSuccess: () => {
      // Invalidate both applications and jobs queries
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.JOBS.APPLICATIONS });
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
  });

  // ─── Withdraw Application Mutation ─────────────────────────────────────────

  const withdrawMutation = useMutation<void, CrewAheadApiError, string>({
    mutationFn: withdrawApplicationApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.JOBS.APPLICATIONS });
    },
  });

  // ─── Save Job Mutation ─────────────────────────────────────────────────────

  const saveJobMutation = useMutation<void, CrewAheadApiError, string, { previousSaved: { count: number; results: JobPost[] } | undefined }>({
    mutationFn: async (jobId) => { await saveJobApi(jobId); },
    onMutate: async (_jobId) => {
      // Optimistically update saved jobs
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.JOBS.SAVED });
      const previousSaved = queryClient.getQueryData<{ count: number; results: JobPost[] }>(
        QUERY_KEYS.JOBS.SAVED
      );

      return { previousSaved };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.JOBS.SAVED });
    },
    onError: (_err, _variables, context) => {
      if (context?.previousSaved) {
        queryClient.setQueryData(QUERY_KEYS.JOBS.SAVED, context.previousSaved);
      }
    },
  });

  // ─── Unsave Job Mutation ───────────────────────────────────────────────────

  const unsaveJobMutation = useMutation<void, CrewAheadApiError, string, { previousSaved: { count: number; results: JobPost[] } | undefined }>({
    mutationFn: async (jobId) => { await unsaveJobApi(jobId); },
    onMutate: async (jobId) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.JOBS.SAVED });
      const previousSaved = queryClient.getQueryData<{ count: number; results: JobPost[] }>(
        QUERY_KEYS.JOBS.SAVED
      );

      // Optimistically remove from saved
      if (previousSaved) {
        queryClient.setQueryData(QUERY_KEYS.JOBS.SAVED, {
          count: Math.max(0, previousSaved.count - 1),
          results: previousSaved.results.filter((job) => job.id !== Number(jobId)),
        });
      }

      return { previousSaved };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.JOBS.SAVED });
    },
    onError: (_err, _variables, context) => {
      if (context?.previousSaved) {
        queryClient.setQueryData(QUERY_KEYS.JOBS.SAVED, context.previousSaved);
      }
    },
  });

  // ─── Job Alert Mutations ───────────────────────────────────────────────────

  const createAlertMutation = useMutation<JobAlert, CrewAheadApiError, JobAlertCreateData>({
    mutationFn: (data: JobAlertCreateData) => createJobAlertApi(data as Partial<JobAlert>),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.JOBS.ALERTS });
    },
  });

  const updateAlertMutation = useMutation<
    JobAlert,
    CrewAheadApiError,
    { alertId: string; data: Partial<JobAlertCreateData> }
  >({
    mutationFn: ({ alertId, data }) => updateJobAlertApi(Number(alertId), data as Partial<JobAlert>),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.JOBS.ALERTS });
    },
  });

  const deleteAlertMutation = useMutation<void, CrewAheadApiError, string>({
    mutationFn: (alertId) => deleteJobAlertApi(Number(alertId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.JOBS.ALERTS });
    },
  });

  const toggleAlertMutation = useMutation<
    JobAlert,
    CrewAheadApiError,
    { alertId: string; isActive: boolean }
  >({
    mutationFn: ({ alertId, isActive }) => toggleJobAlertApi(alertId, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.JOBS.ALERTS });
    },
  });

  // ─── Callbacks ─────────────────────────────────────────────────────────────

  const apply = useCallback(
    async (jobId: string, data: JobApplicationCreateData = {}): Promise<JobApplication> => {
      return applyMutation.mutateAsync({ jobId, data });
    },
    [applyMutation]
  );

  const withdrawApplication = useCallback(
    async (applicationId: string): Promise<void> => {
      await withdrawMutation.mutateAsync(applicationId);
    },
    [withdrawMutation]
  );

  const saveJob = useCallback(
    async (jobId: string): Promise<void> => {
      await saveJobMutation.mutateAsync(jobId);
    },
    [saveJobMutation]
  );

  const unsaveJob = useCallback(
    async (jobId: string): Promise<void> => {
      await unsaveJobMutation.mutateAsync(jobId);
    },
    [unsaveJobMutation]
  );

  const checkIsJobSaved = useCallback(
    async (jobId: string): Promise<boolean> => {
      return isJobSavedApi(jobId);
    },
    []
  );

  const createJobAlert = useCallback(
    async (data: JobAlertCreateData): Promise<JobAlert> => {
      return createAlertMutation.mutateAsync(data);
    },
    [createAlertMutation]
  );

  const updateJobAlert = useCallback(
    async (alertId: string, data: Partial<JobAlertCreateData>): Promise<JobAlert> => {
      return updateAlertMutation.mutateAsync({ alertId, data });
    },
    [updateAlertMutation]
  );

  const deleteJobAlert = useCallback(
    async (alertId: string): Promise<void> => {
      await deleteAlertMutation.mutateAsync(alertId);
    },
    [deleteAlertMutation]
  );

  const toggleJobAlertFn = useCallback(
    async (alertId: string, isActive: boolean): Promise<JobAlert> => {
      return toggleAlertMutation.mutateAsync({ alertId, isActive });
    },
    [toggleAlertMutation]
  );

  const refetchJobs = useCallback(async (): Promise<void> => {
    await refetchJobsQuery();
  }, [refetchJobsQuery]);

  // ─── Derived Data ──────────────────────────────────────────────────────────

  const jobs = useMemo(() => jobsData?.results ?? [], [jobsData]);
  const jobsPagination = useMemo(
    () =>
      jobsData
        ? { count: jobsData.count, next: null, previous: null }
        : null,
    [jobsData]
  );

  const applications = useMemo(() => applicationsData?.results ?? [], [applicationsData]);
  const applicationsPagination = useMemo(
    () =>
      applicationsData
        ? { count: applicationsData.count, next: null, previous: null }
        : null,
    [applicationsData]
  );

  const savedJobs = useMemo(
    () => savedJobsRaw?.results ?? [],
    [savedJobsRaw]
  );
  const savedJobsPagination = useMemo(
    () =>
      savedJobsRaw
        ? { count: savedJobsRaw.count, next: null as string | null, previous: null as string | null }
        : null,
    [savedJobsRaw]
  );

  const jobAlerts = useMemo(() => jobAlertsData ?? [], [jobAlertsData]);

  const isMutatingAlert =
    createAlertMutation.isPending ||
    updateAlertMutation.isPending ||
    deleteAlertMutation.isPending ||
    toggleAlertMutation.isPending;

  return {
    // Job discovery
    jobs,
    jobsPagination,
    isJobsLoading,
    jobsError: (jobsError as CrewAheadApiError | null) ?? null,
    refetchJobs,

    // Single job
    job: singleJobData ?? null,
    isJobLoading: isSingleJobLoading,
    jobError: (singleJobError as CrewAheadApiError | null) ?? null,

    // Applications
    apply,
    isApplying: applyMutation.isPending,
    applications,
    applicationsPagination,
    isApplicationsLoading,
    withdrawApplication,
    isWithdrawing: withdrawMutation.isPending,

    // Saved jobs
    savedJobs,
    savedJobsPagination,
    isSavedJobsLoading,
    saveJob,
    unsaveJob,
    isSaving: saveJobMutation.isPending || unsaveJobMutation.isPending,
    isJobSaved: checkIsJobSaved,

    // Job alerts
    jobAlerts,
    isJobAlertsLoading,
    createJobAlert,
    isCreatingAlert: createAlertMutation.isPending,
    updateJobAlert,
    deleteJobAlert,
    toggleJobAlert: toggleJobAlertFn,
    isMutatingAlert,
  };
}